import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type { CyclesResponse, PeriodCardDto } from '../../contracts/cycles'

/**
 * Циклы считаются один раз на все экраны: вкладка, разбор периода и жизненная
 * шкала читают один и тот же ответ /api/cycles. Держим его в модуле, как карты
 * в chartsData: «плитка → период → назад» иначе пересчитывает весь день заново.
 *
 * Кэш живёт до перезагрузки мини-аппа и привязан к дате ответа: если человек
 * оставил приложение открытым через полночь, данные всё равно вчерашние —
 * это осознанный размен, сутки внутри одной сессии Атлас не переигрывает.
 */
let cache: CyclesResponse | null = null

/**
 * Собранные личные карточки периодов. Раскрытие «Личной карточки» живёт внутри
 * Collapsible: без этого кэша каждое схлопывание теряет уже написанный текст и
 * следующее открытие снова идёт на сервер за карточкой, которая стоит денег.
 * Ключ — период плюс ключ периода из ответа: сменился месяц — карточка другая.
 */
const cardCache = new Map<string, PeriodCardDto>()

export function cachedPeriodCard(key: string): PeriodCardDto | null {
  return cardCache.get(key) ?? null
}

export function rememberPeriodCard(key: string, card: PeriodCardDto): void {
  cardCache.set(key, card)
}

export function resetCycles(): void {
  cache = null
  cardCache.clear()
}

export function useCycles(): { data: CyclesResponse | null; error: string } {
  const [data, setData] = useState<CyclesResponse | null>(cache)
  const [error, setError] = useState('')

  useEffect(() => {
    if (cache) return undefined
    let cancelled = false
    api
      .get<CyclesResponse>('/api/cycles')
      .then((result) => {
        cache = result
        if (!cancelled) setData(result)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'Циклы пока не собрались')
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { data, error }
}
